import { FC, useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { UserRoundPlus, Users } from 'lucide-react';
import ClassesRosterStudentDataTable from './ClassesRosterDataTable/ClassesRosterStudentDataTable';
import { Class, ClassUser } from '../../models/Class';
import { StudentClasses } from '../../models/StudentClasses';
import { TeacherClasses } from '../../models/TeacherClasses';
import { Roles } from '../../models/User';
import { useLazyLoadQuery } from 'react-relay';
import { Toast } from 'primereact/toast';
import { classById } from '../../graphQLOperations/Class';
import { ClassByIdQuery } from '../../graphQLOperations/__generated__/ClassByIdQuery.graphql';
import ClassManageCoTeachersDialog from './ClassesRosterWizard/ClassManageCoTeachersDialog';
import ClassManageStudentsDialog from './ClassesRosterWizard/ClassManageStudentsDialog';

export const NO_ASSIGNED_ROADMAP = 'No Assigned Roadmap';
interface Props {
  selectedClass: Class | null;
  userOwnerOfClass?: boolean;
  userCreatorOfClass?: boolean;
  setSelectedClass: (c: Class) => void;
  setRefetchSingleClassKey: (key: number) => void;
  refetchSingleClassKey: number;
}

export interface StudentFiltered {
  studentId: number | string;
  studentName?: string;
  studentFirstName: string;
  studentLastName: string;
  publicUserId: string;
  gradeName: string;
  classesName: string;
}

const ClassesRostersRightSide: FC<Props> = ({
  selectedClass,
  userOwnerOfClass,
  setSelectedClass,
  setRefetchSingleClassKey,
  refetchSingleClassKey,
}) => {
  const toast = useRef<Toast>(null);
  const [teachersInClass, setTeachersInClass] = useState<TeacherClasses[]>([]);
  const [refetchStudentKey, setRefetchStudentKey] = useState(0);
  const [studentsInClass, setStudentsInClass] = useState<StudentClasses[]>([]);
  const [manageCoTeachersVisible, setManageCoTeachersVisible] = useState(false);
  const [manageStudentsVisible, setManageStudentsVisible] = useState(false);

  const singleClassData = useLazyLoadQuery<ClassByIdQuery>(
    classById,
    {
      classId: selectedClass?.id || '',
      skip: !selectedClass?.id,
    },
    {
      fetchKey: selectedClass?.id ? `${selectedClass.id}_${refetchSingleClassKey}` : '',
      fetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    if (singleClassData?.class?.users) {
      setSelectedClass({ ...(selectedClass as Class), users: singleClassData?.class?.users as ClassUser[] });
    }
  }, [singleClassData]);

  useEffect(() => {
    const teachers =
      selectedClass?.users
        ?.filter(user => user?.isActive)
        ?.filter(user => user?.roles?.some(role => role?.name === Roles.TEACHER))
        ?.map(teacher => ({
          teacherId: teacher?.id ?? '',
          teacherFirstName: teacher?.firstName ?? '',
          teacherLastName: teacher?.lastName ?? '',
          siteName: teacher?.sites?.map(site => site?.name)?.join(', ') ?? '',
          classesName:
            teacher?.userClasses
              ?.filter(uc => uc.isActive)
              .map(uc => (uc?.class.section ? `${uc?.class.name} - ${uc.class.section}` : uc?.class.name))
              ?.join(', ') ?? '',
        })) ?? [];
    const students =
      selectedClass?.users
        ?.filter(user => user?.isActive)
        ?.filter(user => user?.roles?.some(role => role?.name === Roles.STUDENT))
        ?.map(student => ({
          studentId: student?.id ?? '',
          studentFirstName: student?.firstName ?? '',
          studentLastName: student?.lastName ?? '',
          publicUserId: student?.publicUserId ?? '',
          gradeName: student?.gradeLevels?.map(grade => grade?.name)?.join(', ') ?? '',
          currentRoadmapName: (() => {
            const currentRoadmap = student.studentTargetGroups?.find(tg => tg.isCurrent);
            if (currentRoadmap) {
              return currentRoadmap.targetGroup ? currentRoadmap.targetGroup.name : NO_ASSIGNED_ROADMAP;
            } else {
              return NO_ASSIGNED_ROADMAP;
            }
          })(),
          classesName:
            student?.userClasses
              ?.filter(uc => uc.isActive)
              .map(uc => (uc?.class.section ? `${uc?.class.name} - ${uc.class.section}` : uc?.class.name))
              ?.join(', ') ?? '',
        })) ?? [];
    if (teachers) setTeachersInClass(teachers);
    if (students) setStudentsInClass(students);
  }, [selectedClass]);

  const teacherTitleEndContent = (
    <Button
      label="Manage co-teachers"
      rounded
      outlined
      disabled={Boolean(selectedClass?.externalSync)}
      aria-label="Manage co-teachers"
      onClick={() => setManageCoTeachersVisible(true)}
      id="manageCoTeachersButton"
    />
  );

  const studentTitleStartContent = () => (
    <h2
      className="flex align-items-center gap-sm tw-not-italic"
      style={{ fontFamily: "'Atkinson Hyperlegible', sans-serif", lineHeight: '26px' }}
    >
      <Users size={16} color="#A6A6A6" aria-hidden />
      Students
      <span className="tw-inline-flex tw-items-center tw-justify-center tw-h-6 tw-min-w-7 tw-px-1 tw-rounded-lg tw-bg-neutral-100 tw-text-base tw-font-bold tw-text-[#393939]">
        {studentsInClass.length}
      </span>
    </h2>
  );

  const studentTitleEndContent = (
    <Button
      label="Manage students"
      rounded
      outlined
      disabled={Boolean(selectedClass?.externalSync)}
      aria-label="Manage students"
      onClick={() => setManageStudentsVisible(true)}
      id="manageStudentsButton"
    />
  );

  return (
    <>
      <Toast ref={toast} />
      <div
        className="flex flex-column col-10 col-offset-1 lg:col-9 lg:col-offset-0 nc_sidebar-main"
        data-testid="container"
      >
        {selectedClass?.name ? (
          <>
            <div className="flex align-items-center justify-content-between nc_block">
              <span className="flex align-items-center gap-sm">
                <UserRoundPlus size={16} style={{ color: '#B82828' }} aria-hidden />
                <b style={{ color: '#B82828' }}>Co-teacher</b>
                {teachersInClass.length > 0 ? (
                  <span className="tw-font-bold">
                    {teachersInClass.map(t => `${t.teacherFirstName} ${t.teacherLastName}`).join(' | ')}
                  </span>
                ) : (
                  <span className="tw-text-sm tw-italic tw-font-bold tw-text-[#686868]">No co-teachers added</span>
                )}
              </span>
              {userOwnerOfClass && teacherTitleEndContent}
            </div>
            <div className="flex flex-column nc_block nc_students-card">
              <Toolbar
                start={studentTitleStartContent}
                end={userOwnerOfClass ? studentTitleEndContent : undefined}
                className="nc_page-header nc_students-toolbar"
              />
              <ClassesRosterStudentDataTable
                setRefetchSingleClassKey={setRefetchSingleClassKey}
                studentsList={studentsInClass}
                setRefetchStudentKey={setRefetchStudentKey}
                userOwnerOfClass={userOwnerOfClass}
                classUsers={selectedClass?.users}
                isClassExternal={Boolean(selectedClass?.externalSync)}
                classId={selectedClass?.id}
                key={refetchStudentKey}
              />
            </div>
            <ConfirmDialog />
            {manageCoTeachersVisible && (
              <ClassManageCoTeachersDialog
                visible
                onHide={() => setManageCoTeachersVisible(false)}
                selectedClass={selectedClass}
                toast={toast}
                onSaved={() => setRefetchSingleClassKey(new Date().getTime())}
              />
            )}
            {manageStudentsVisible && (
              <ClassManageStudentsDialog
                visible
                onHide={() => setManageStudentsVisible(false)}
                selectedClass={selectedClass}
                toast={toast}
                onSaved={() => setRefetchSingleClassKey(new Date().getTime())}
              />
            )}
          </>
        ) : (
          <div className="lg:flex lg:flex-row lg:pt-7 lg:align-items-center gap-md flex-column">
            <i className="nc_icon-nc_back icon-md lg:flex hidden"></i>
            <i className="nc_icon-nc_up icon-md lg:hidden"></i>
            <h3>
              Select a class to start.<br></br>If there are no classes, use the button above to create your first one!
            </h3>
          </div>
        )}
      </div>
    </>
  );
};

export default ClassesRostersRightSide;
